const canUseEnv = typeof process !== "undefined" && Boolean(process.env);

export const isUnitTest = (() => {
  // https://stackoverflow.com/questions/50940640/how-to-determine-if-jest-is-running-the-code-or-not
  if (typeof process !== "undefined" && process.env.JEST_WORKER_ID) {
    return true;
  }
  return false;
})();

export const isDevelopment =
  !isUnitTest && canUseEnv && process.env.NODE_ENV === "development";

const shouldUseLocalHttps =
  typeof process !== "undefined" && process.env.REPLO_LOCAL_HTTPS === "true";
const shouldUseLocalProdData =
  typeof process !== "undefined" &&
  process.env.REPLO_LOCAL_PROD_DATA === "true";

// NOTE (Kevin 2025-12-22, REPL-21371): Default ports use sequential offsets
// from base 3000: editor=3000, publisher=3001, mirror=3002, launchpad=3003,
// websocket=3004, ngrokProxy=3005, etc.

/**
 * Check if the current hostname is an ngrok domain
 */
function isNgrokDomain(hostname: string): boolean {
  return hostname.endsWith(".ngrok.app");
}

/**
 * Extract the dev name from an ngrok domain.
 * e.g., "replo-dev-ben-editor.ngrok.app" -> "ben"
 */
function getNgrokDevName(hostname: string): string | null {
  const match = hostname.match(/^replo-dev-([^-]+)-\w+\.ngrok\.app$/);
  return match?.[1] ?? null;
}

// WebSocket URL for multiplayer editor functionality
export const WEBSOCKET_BASE_URL = isDevelopment
  ? `ws://localhost:${process.env.REPLO_DEV_WEBSOCKET_PORT ?? "3004"}`
  : "wss://multiplayer.reploapi.com";

/**
 * The default values we should provide to the `credentials` option of fetch()
 * when we're calling our backend in the editor.
 */
export const defaultFetchCredentials = (() => {
  // Note (Noah, 2025-07-18, REPL-18040): "include" is the default, because we
  // want requests to our backend to respect the Set-Cookie header so that cookies
  // can propagate to subsequent requests. "same-origin" does not work because
  // dashboard.replo.app and api.replo.app are different origins.
  //
  // This is fine for CSRF, because our cookies have SameSite=Lax so they'll never
  // be sent by malicious impersonating javascript, and we don't even use cookies
  // for auth anyway (currently).
  return "include" as const;
})();

// NOTE (Patrick, 2026-02-25): Vercel preview deployments have dynamic hostnames
// that can't be whitelisted in Google Cloud Console. We detect these to route to
// the staging backend and use the Google auth popup proxy.
export function isVercelPreviewDeployment(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return process.env.VERCEL_ENV === "preview";
}

// (Patrick, 2026-03-11): The stable origin for the Google OAuth proxy page.
// This domain is already whitelisted in Google Cloud Console as an authorized
// JavaScript origin. Preview deployments open a popup to this origin to
// complete Google sign-in.
export const GOOGLE_AUTH_PROXY_ORIGIN = "https://dashboard.replo.app";

export function getPublisherUrl() {
  if (localStorage.getItem("replo.debug.useBackendStaging") === "true") {
    return "https://replo-publisher-staging.fly.dev";
  }

  const hostname = window.location.hostname;

  // Handle ngrok domains for mobile development
  if (isDevelopment && isNgrokDomain(hostname)) {
    const devName = getNgrokDevName(hostname);
    if (devName) {
      return `https://replo-dev-${devName}-publisher.ngrok.app`;
    }
  }

  // Note (Noah, 2025-07-17): isDevelopment is actually false in e2e tests, so we have
  // to check for localhost. Pretty sure there's a better way to do this
  if (
    !shouldUseLocalProdData &&
    (isDevelopment || hostname === "localhost" || hostname === "127.0.0.1")
  ) {
    const port = process.env.REPLO_DEV_PUBLISHER_PORT ?? "3001";
    const protocol = shouldUseLocalHttps ? "https" : "http";
    return `${protocol}://${hostname}:${port}`;
  }

  // NOTE (Patrick, 2026-02-25): Vercel preview deployments should use the
  // staging backend so PR previews don't hit production.
  if (isVercelPreviewDeployment()) {
    return "https://replo-publisher-staging.fly.dev";
  }

  return "https://api.replo.app";
}

export function getDashboardUrl() {
  // Note (Noah, 2025-07-17): isDevelopment is actually false in e2e tests, so we have
  // to check for localhost. Pretty sure there's a better way to do this
  if (
    isDevelopment ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  ) {
    const port = process.env.REPLO_DEV_EDITOR_PORT ?? "3000";
    return `http://localhost:${port}`;
  }
  return "https://dashboard.replo.app";
}

export const SHOPIFY_APP_LISTING_URL = "https://apps.shopify.com/alchemy";
